'use client';

import clsx from 'clsx';
import React, { useEffect, useId, useRef } from 'react';
import { checkboxBoxStyles, checkboxContainerStyles, checkboxIconStyles, checkboxIndeterminateStyles, visuallyHiddenStyles } from './styles';
import CheckIcon from '/public/icons/check.svg';
import DashIcon from '/public/icons/minus.svg';

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  labelClassName?: string;
  dashIcon?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'secondary' | 'buttonDefault' | 'transparent';
  size?: 'small' | 'xsmall' | 'medium' | 'large';
  helperText?: string;
}

const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked = false,
  indeterminate = false,
  onChange,
  className,
  labelClassName,
  dashIcon = false,
  id,
  disabled = false,
  variant = 'buttonDefault',
  size = 'medium',
  required,
  helperText,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const Icon = dashIcon ? DashIcon : CheckIcon;
  const generatedId = useId();
  const uniqueId = id || `checkbox-${generatedId}`;
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (onChange) {
      onChange(event);
    }
  };

  return (
    <label htmlFor={uniqueId} className={checkboxContainerStyles({ disabled, className })}>
      <input
        type="checkbox"
        id={uniqueId}
        ref={inputRef}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className={visuallyHiddenStyles()}
        {...props}
      />
      <span
        className={checkboxBoxStyles({
          intent: variant,
          size,
          selected: checked || indeterminate,
          disabled: Boolean(disabled),
        })}
      >
        {checked && !indeterminate && <Icon className={checkboxIconStyles({ intent: variant, size: size })} />}
        {indeterminate && <div className={checkboxIndeterminateStyles({ intent: variant })} />}
      </span>
      {label && (
        <span
          className={clsx(
            'relative text-body-regular ml-3 mt-[1px] text-grey-700',
            labelClassName
          )}
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
          {helperText && <p className="text-blue-300 text-body-small mt-[6px]">{helperText}</p>}
        </span>
      )}
    </label>
  );
};

export default Checkbox;